import React from 'react';
import { observer } from 'mobx-react';
import { Col } from 'reactstrap';
import { mapK8sCostTypesToDisplay } from 'shared/constants/k8sConstants';
import Spinner from 'shared/components/andtComponents/Spinner';
import { withInvoiceFiltersContextConsumer } from 'invoices/contexts/InvoiceFiltersContext';
import ControlledToggleButton from 'shared/components/buttons/ControlledToggleButton';
import FilterContentListNew from 'shared/components/FilterSidebar/FilterContentList';
import { SearchFilters } from 'recommendationsNew/components/heatMap/heatMapFilters/additionalFilters/searchFilters';
import { useRootStore } from 'app/contexts/RootStoreContext';
import styles from './FieldSidebarFilter.module.scss';
import { getLabel } from './filterUtil';
import Button from '../andtComponents/Button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@pileus-cloud/anodot-frontend-common';
import ConnectK8s from 'kubernetes/containers/KubernetesPreview/components/ConnectK8s';
import ConnectK8sLegacy from 'kubernetes/containers/KubernetesPreview/components/OldConnectK8s';
import checkFeatureFlag from 'shared/utils/featureFlagUtil';
import { ACCOUNT_FEATURES } from 'users/constants/usersConstants';
import { useNavigate } from 'react-router';
import { Routes } from 'shared/constants/routes';
import { AwsCommonFields } from 'shared/constants/awsConstants';
import Checkbox from 'shared/components/andtComponents/Checkbox';
import Tooltip from '~/shared/components/andtComponents/Tooltip.jsx';

const filterFiltersList = (filtersList, searchStr, currDispUserCloudAccountType) => {
  if (!searchStr) {
    return filtersList;
  }
  const newMap = new Map();
  [...filtersList.entries()].forEach(([key, value]) => {
    const fieldLabel = getLabel(key, currDispUserCloudAccountType)?.toLowerCase();
    if (fieldLabel?.includes(searchStr.toLowerCase())) {
      newMap.set(key, value);
    }
  });
  return newMap;
};

const renderK8sCostType = (props) => {
  const { isForK8S, selectedK8SCostType = null, k8sCostTypeItems = [], handleK8SCostTypeChecked = null } = props;
  if (!isForK8S) {
    return null;
  }
  return (
    <div className="field-sidebar-filter">
      <div className="field-name-container mb-2">
        <p className="field-name">K8s Cost Types</p>
      </div>
      {k8sCostTypeItems.map((costType) => (
        <Col key={costType} className="ps-0">
          <div className="toggler-with-text-wrapper ml-0">
            <Col className="toggler-inner-item flex-grow-0">
              <ControlledToggleButton
                checked={selectedK8SCostType.includes(costType)}
                selectedItem={costType}
                handler={handleK8SCostTypeChecked}
              />
            </Col>
            <Col className="toggler-inner-item ms-3">{mapK8sCostTypesToDisplay.get(costType)}</Col>
          </div>
        </Col>
      ))}
    </div>
  );
};

const FiltersSidebarContainer = ({ ...props }) => {
  const [filtersSearchString, setFiltersSearchString] = React.useState('');
  const { usersStore } = useRootStore();

  const {
    fieldToFieldDistinctValuesMap,
    filtersLoading,
    selectedOptionsMap,
    isForK8S,
    isForCue,
    className,
    showTitle,
    fieldsWidth,
    hideSearch,
    infoLink,
    handleFilterChange,
    tabs,
  } = props;
  const navigate = useNavigate();

  const fieldToFieldDistinctMapFiltered = React.useMemo(() => {
    return filterFiltersList(
      fieldToFieldDistinctValuesMap,
      filtersSearchString,
      usersStore.currDispUserCloudAccountType,
    );
  }, [usersStore.currDispUserCloudAccountType, fieldToFieldDistinctValuesMap, filtersSearchString]);
  const k8sOnboardingEnabled = checkFeatureFlag(usersStore, ACCOUNT_FEATURES.K8S_ONBOARDING);
  const k8sEnabled = checkFeatureFlag(usersStore, ACCOUNT_FEATURES.K8S);

  const renderK8SPreview = () => {
    return k8sOnboardingEnabled ? (
      <ConnectK8s onOpenWizard={() => navigate(Routes.K8S_PREVIEW + '?start-onboarding=1')} />
    ) : (
      <ConnectK8sLegacy />
    );
  };
  const renderFiltersList = (filtersList, fieldToFieldDistinctValuesProps, isCostTypeFilter) => (
    <>
      {!hideSearch && (
        <div className={styles.filtersSearch}>
          <SearchFilters
            placeholder="Search Filter"
            searchValue={filtersSearchString}
            onSearchChange={setFiltersSearchString}
          />
          {infoLink && (
            <Button
              onClick={() => window.open(infoLink, '_blank')}
              text="Learn more"
              isTextButton
              overrideStyles={{ width: 'min-content' }}
            />
          )}
        </div>
      )}
      <div className="filters-collapse-inner-wrapper">
        {renderK8sCostType(props)}
        <FilterContentListNew
          fieldToFieldDistinctValuesMap={filtersList}
          selectedOptionsMap={selectedOptionsMap}
          handleFilterChange={handleFilterChange}
          isForK8S={isForK8S}
          generalProps={{
            ...props,
            fieldToFieldDistinctValuesProps,
            isCostTypeFilter,
          }}
          fieldsWidth={fieldsWidth}
        />
      </div>
    </>
  );
  return (
    <div
      className={`filters-sidebar-container ${className} ${isForCue ? 'cue' : ''} `}
      style={{ transform: 'scaleX(-1)' }}
    >
      <div style={{ transform: 'scaleX(-1)', marginLeft: 1 }}>
        {showTitle ? <h3 className="title">{`${'Filters'.toUpperCase()}:`}</h3> : null}
        {tabs?.length ? (
          <>
            {k8sEnabled && (
              <Tooltip enterNextDelay={600} title="Filter out all data that is not related to Kubernetes charges.">
                <div>
                  <Checkbox
                    primary
                    className={styles.k8sCostCheckbox}
                    isChecked={selectedOptionsMap
                      .get(AwsCommonFields.COST_GLOBAL_TYPE)
                      ?.some((opt) => opt.value === 'k8s')}
                    onChange={(checked) => {
                      if (checked) {
                        handleFilterChange(AwsCommonFields.COST_GLOBAL_TYPE, [{ label: 'k8s', value: 'k8s' }]);
                      } else {
                        handleFilterChange(AwsCommonFields.COST_GLOBAL_TYPE, []);
                      }
                    }}
                    text="Display Only K8s-Related Data"
                  />
                </div>
              </Tooltip>
            )}
            <Tabs defaultValue={tabs[0].value}>
              <TabsList className={styles.navbar}>
                {tabs.map((tab) => (
                  <TabsTrigger key={tab.value} value={tab.value} className={styles.navbarButton}>
                    {tab.label} (
                    {
                      Array.from(selectedOptionsMap.keys()).filter(
                        (k) => tab.filtersList.has(k) && selectedOptionsMap.get(k).length,
                      ).length
                    }
                    )
                  </TabsTrigger>
                ))}
              </TabsList>
              {tabs.map((tab) => (
                <TabsContent value={tab.value} key={tab.value} className={styles.tabContent}>
                  {tab.value === 'k8s' && !k8sEnabled
                    ? renderK8SPreview()
                    : renderFiltersList(
                        filterFiltersList(
                          tab.filtersList,
                          filtersSearchString,
                          usersStore.currDispUserCloudAccountType,
                        ),
                        tab.fieldToFieldDistinctValuesProps,
                        tab.isCostTypeFilter,
                      )}
                </TabsContent>
              ))}
            </Tabs>
          </>
        ) : (
          <div className={styles.flatList}>
            {renderFiltersList(
              fieldToFieldDistinctMapFiltered,
              props.fieldToFieldDistinctValuesProps,
              props.isCostTypeFilter,
            )}
          </div>
        )}
        {filtersLoading && (
          <div className={styles.loadingScreen}>
            <Spinner />
          </div>
        )}
      </div>
    </div>
  );
};

const ObserverFiltersSidebarContainer = withInvoiceFiltersContextConsumer(observer(FiltersSidebarContainer));
export default ObserverFiltersSidebarContainer;
